'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { toast } from 'sonner';
import type { PrV2CommentThreads } from './use-pr-v2-comment-threads';
import { usePrV2RepositoryIdResolution } from '../shared/use-pr-v2-repository-id';
import { isDisplayedCubicLogin } from './pr-v2-cubic-review-utils';
import { MAX_VIOLATIONS_PER_BATCH } from '@/constants/fix-all';
import { useQueryAIReviewSettings } from '@/data/ai-review/settings/use-query-ai-review-settings';
import { useCodingAgentsAccess } from '@/data/billing/use-coding-agents-access';
import { requestPrFix } from '@/data/fix/request-pr-fix';
import { DEFAULT_AUTO_FIX_PROVIDER } from '@/lib/ai-review-settings/schema';
import { buildFixPRPath, FIX_ENTRY_SOURCE, FIX_TYPE } from '@/lib/ai/coding-agent-service/fix-tracking';
import { FIX_BUTTON_SOURCE, trackFixButtonClicked } from '@/lib/analytics/fix-button-clicked';
import { formatCountedNoun } from '@/lib/auto-fix-loop/outcome-copy';
import { resolveFixProviderButtonCopy } from '@/lib/pr-v2/fix-provider-button-copy';
import { usePrStore } from '@/lib/stores/pr-store';
export type PrV2FixAllState = 'ready' | 'locked' | 'unavailable';

/**
 * Fix all: fixes cubic's open comments from its latest actionable review with the repository's fix
 * provider. The comments popover's wrench button and the command menu both run it.
 */
export function usePrV2FixAll(t0) {
  const $ = _c(54);
  const {
    owner,
    repo,
    number,
    threads: t1
  } = t0;
  const {
    cubicReviewSummary,
    botThreadRows
  } = t1;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    installationId,
    repositoryId: storeRepositoryId
  } = usePrStore();
  const {
    canUseCodingAgents,
    isError: isCodingAgentsAccessError,
    isLoading: isCodingAgentsAccessLoading
  } = useCodingAgentsAccess(installationId);
  const t2 = Boolean(installationId);
  let t3;
  if ($[0] !== installationId || $[1] !== owner || $[2] !== repo || $[3] !== t2) {
    t3 = {
      owner,
      repo,
      installationId,
      enabled: t2
    };
    $[0] = installationId;
    $[1] = owner;
    $[2] = repo;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    repositoryId: fallbackRepositoryId,
    repositoryIsPrivate,
    isResolving: isRepositoryIdResolving
  } = usePrV2RepositoryIdResolution(t3);
  const repositoryId = storeRepositoryId ?? fallbackRepositoryId;
  const t4 = repositoryId != null;
  let t5;
  if ($[5] !== installationId || $[6] !== repositoryId || $[7] !== repositoryIsPrivate || $[8] !== t4) {
    t5 = {
      installationId,
      repositoryId,
      repositoryIsPrivate,
      enabled: t4
    };
    $[5] = installationId;
    $[6] = repositoryId;
    $[7] = repositoryIsPrivate;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    settings: aiReviewSettings,
    isLoading: isAiReviewSettingsLoading
  } = useQueryAIReviewSettings(t5);
  let cubicFixAllUrl;
  let fixAllState;
  let fixProviderCopy;
  let isFixProviderResolved;
  let latestActionableReviewSessionId;
  let latestActionableViolationIds;
  let t6;
  if ($[10] !== aiReviewSettings?.autoFixProvider || $[11] !== botThreadRows || $[12] !== canUseCodingAgents || $[13] !== cubicReviewSummary?.fixAllOutstandingCount || $[14] !== cubicReviewSummary?.latestActionableReviewSessionId || $[15] !== cubicReviewSummary?.latestActionableViolationIds || $[16] !== cubicReviewSummary?.outstandingComments || $[17] !== isAiReviewSettingsLoading || $[18] !== isCodingAgentsAccessError || $[19] !== isCodingAgentsAccessLoading || $[20] !== isRepositoryIdResolving || $[21] !== number || $[22] !== owner || $[23] !== repo) {
    fixProviderCopy = resolveFixProviderButtonCopy(aiReviewSettings?.autoFixProvider ?? DEFAULT_AUTO_FIX_PROVIDER);
    isFixProviderResolved = !isRepositoryIdResolving && !isAiReviewSettingsLoading;
    const cubicThreadRows = botThreadRows.filter(_temp);
    const hasCubicThreads = cubicThreadRows.length > 0;
    const openCubicComments = hasCubicThreads ? cubicThreadRows.filter(_temp2).length : cubicReviewSummary?.outstandingComments ?? 0;
    const fixAllOutstandingCount = cubicReviewSummary?.fixAllOutstandingCount ?? 0;
    latestActionableReviewSessionId = cubicReviewSummary?.latestActionableReviewSessionId;
    let t7;
    if ($[31] !== cubicReviewSummary?.latestActionableViolationIds) {
      t7 = cubicReviewSummary?.latestActionableViolationIds ?? [];
      $[31] = cubicReviewSummary?.latestActionableViolationIds;
      $[32] = t7;
    } else {
      t7 = $[32];
    }
    latestActionableViolationIds = t7;
    let t8;
    if ($[33] !== latestActionableReviewSessionId || $[34] !== number || $[35] !== owner || $[36] !== repo) {
      t8 = latestActionableReviewSessionId ? buildFixPRPath({
        owner,
        repo,
        pullNumber: number,
        reviewSessionId: latestActionableReviewSessionId,
        entrySource: FIX_ENTRY_SOURCE.CUBIC_UI_TO_CUBIC_UI
      }) : null;
      $[33] = latestActionableReviewSessionId;
      $[34] = number;
      $[35] = owner;
      $[36] = repo;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    cubicFixAllUrl = t8;
    const hasFixAllSession = cubicFixAllUrl !== null && latestActionableViolationIds.length > 0 && (!hasCubicThreads || openCubicComments > 0);
    const isCodingAgentsAccessLocked = !isCodingAgentsAccessLoading && !isCodingAgentsAccessError && !canUseCodingAgents;
    fixAllState = "unavailable";
    if (hasFixAllSession && canUseCodingAgents) {
      fixAllState = "ready";
    } else {
      if (hasFixAllSession && isCodingAgentsAccessLocked) {
        fixAllState = "locked";
      }
    }
    t6 = fixAllState === "unavailable" ? fixProviderCopy.noActionableSessionLabel : fixAllOutstandingCount > 0 && fixAllOutstandingCount !== openCubicComments ? fixProviderCopy.buildFixLatestLabel(formatCountedNoun(fixAllOutstandingCount, "comment")) : fixProviderCopy.fixOutstandingLabel;
    $[10] = aiReviewSettings?.autoFixProvider;
    $[11] = botThreadRows;
    $[12] = canUseCodingAgents;
    $[13] = cubicReviewSummary?.fixAllOutstandingCount;
    $[14] = cubicReviewSummary?.latestActionableReviewSessionId;
    $[15] = cubicReviewSummary?.latestActionableViolationIds;
    $[16] = cubicReviewSummary?.outstandingComments;
    $[17] = isAiReviewSettingsLoading;
    $[18] = isCodingAgentsAccessError;
    $[19] = isCodingAgentsAccessLoading;
    $[20] = isRepositoryIdResolving;
    $[21] = number;
    $[22] = owner;
    $[23] = repo;
    $[24] = cubicFixAllUrl;
    $[25] = fixAllState;
    $[26] = fixProviderCopy;
    $[27] = isFixProviderResolved;
    $[28] = latestActionableReviewSessionId;
    $[29] = latestActionableViolationIds;
    $[30] = t6;
  } else {
    cubicFixAllUrl = $[24];
    fixAllState = $[25];
    fixProviderCopy = $[26];
    isFixProviderResolved = $[27];
    latestActionableReviewSessionId = $[28];
    latestActionableViolationIds = $[29];
    t6 = $[30];
  }
  const description = t6;
  let t7;
  if ($[38] !== aiReviewSettings?.autoFixProvider || $[39] !== cubicFixAllUrl || $[40] !== installationId || $[41] !== latestActionableReviewSessionId || $[42] !== latestActionableViolationIds || $[43] !== number || $[44] !== owner || $[45] !== repo) {
    t7 = async function run() {
      if (!latestActionableReviewSessionId) {
        return;
      }
      if (latestActionableViolationIds.length === 0) {
        toast.error("No actionable issues available to fix");
        return;
      }
      trackFixButtonClicked({
        installationId,
        owner,
        repo,
        pullNumber: number,
        provider: aiReviewSettings?.autoFixProvider ?? DEFAULT_AUTO_FIX_PROVIDER,
        source: FIX_BUTTON_SOURCE.PR_V2_FIX_ALL,
        fixType: FIX_TYPE.FIX_ALL,
        fixReviewSessionId: latestActionableReviewSessionId,
        fixViolationCount: latestActionableViolationIds.length
      });
      if (latestActionableViolationIds.length > MAX_VIOLATIONS_PER_BATCH) {
        window.location.assign(cubicFixAllUrl ?? "#");
        return;
      }
      const toastId = toast.success("Generating fix - this may take a few minutes");
      setIsSubmitting(true);
      const result = await requestPrFix({
        owner,
        repo,
        number,
        reviewSessionId: latestActionableReviewSessionId,
        violationIds: latestActionableViolationIds,
        entrySource: FIX_ENTRY_SOURCE.CUBIC_UI_TO_CUBIC_UI
      });
      setIsSubmitting(false);
      if (result.kind === "already-running") {
        window.location.assign(result.runUrl);
      } else {
        if (result.kind === "failed") {
          toast.dismiss(toastId);
          toast.error(result.message);
        }
      }
    };
    $[38] = aiReviewSettings?.autoFixProvider;
    $[39] = cubicFixAllUrl;
    $[40] = installationId;
    $[41] = latestActionableReviewSessionId;
    $[42] = latestActionableViolationIds;
    $[43] = number;
    $[44] = owner;
    $[45] = repo;
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  const run = t7;
  const t8 = fixAllState === "locked" ? fixProviderCopy.fixAllRequiresProAriaLabel : fixProviderCopy.fixAllLabel;
  let t9;
  if ($[47] !== description || $[48] !== fixAllState || $[49] !== isFixProviderResolved || $[50] !== isSubmitting || $[51] !== run || $[52] !== t8) {
    t9 = {
      state: fixAllState,
      label: t8,
      description,
      isProviderResolved: isFixProviderResolved,
      isSubmitting,
      run
    };
    $[47] = description;
    $[48] = fixAllState;
    $[49] = isFixProviderResolved;
    $[50] = isSubmitting;
    $[51] = run;
    $[52] = t8;
    $[53] = t9;
  } else {
    t9 = $[53];
  }
  return t9;
}
function _temp2(row_0) {
  return row_0.status === "open";
}
function _temp(row) {
  return isDisplayedCubicLogin(row.userLogin);
}
export type PrV2FixAll = ReturnType<typeof usePrV2FixAll>;